/**
 * @fileoverview Global Badge Celebration Overlay
 * 
 * Full-screen overlay that celebrates newly unlocked badges one at a time.
 * Renders at the app root level so it works on any page.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useBadgeCelebrationQueue } from '@/hooks/useBadgeCelebrationQueue';
import { getTextureStyle } from '@/components/profile/BadgesCard';
import { triggerHaptic } from '@/hooks/useHaptics';
import { useAuth } from '@/hooks/useAuth';

function getTextureBackgroundSize(texture: string): string | undefined {
  switch (texture) {
    case 'grid': return '6px 6px';
    case 'paper': return '5px 5px, 7px 7px';
    case 'sand': return '4px 4px';
    default: return undefined;
  }
}

export function BadgeCelebrationOverlay() {
  const { user } = useAuth();
  const { currentBadge, dismiss } = useBadgeCelebrationQueue();
  const navigate = useNavigate();

  // Fire confetti + haptics on each new badge
  useEffect(() => {
    if (!currentBadge) return;

    triggerHaptic('success');
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.5 },
      colors: ['#f472b6', '#facc15', '#60a5fa', '#34d399'],
    });
  }, [currentBadge]);

  if (!user) return null;

  const badge = currentBadge?.badge;

  return (
    <AnimatePresence>
      {badge && (
        <motion.div
          key={badge.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center"
          onClick={dismiss}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Content */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 12, stiffness: 200 }}
            className="relative flex flex-col items-center gap-4"
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
              navigate('/profile');
            }}
          >
            {/* Badge circle */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-28 h-28 rounded-full shadow-2xl flex items-center justify-center overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${badge.gradientFrom}, ${badge.gradientTo})`,
              }}
            >
              {/* Texture overlay */}
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  backgroundImage: getTextureStyle(badge.texture),
                  backgroundSize: getTextureBackgroundSize(badge.texture),
                }}
              />
              <badge.icon className="w-12 h-12 text-white drop-shadow-md relative z-10" />
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <p className="text-xs font-medium text-white/60 uppercase tracking-widest mb-1">
                Badge Unlocked
              </p>
              <h2 className="text-2xl font-bold text-white">{badge.name}</h2>
              <p className="text-sm text-white/70 mt-1 max-w-[240px]">{badge.description}</p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xs text-white/40 mt-2"
            >
              Tap to view
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
